import { useTranslation } from "react-i18next"

import { Skeleton } from "@/components/ui/skeleton"

import { UsageErrorState } from "./UsageErrorState"
import { useUsageNode } from "../../hooks/use-slide-sense-usage"

interface Props {
  type: "folder" | "deck" | "slide" | null
  id: string | null
  onClose: () => void
}

export function UsageNodeDrawer({ type, id, onClose }: Props) {
  const { t } = useTranslation()
  const { data, isLoading, isError, isFetching, refetch } = useUsageNode(type, id)

  if (!type || !id) return null

  return (
    <aside className="bg-background fixed top-16 right-0 z-30 h-[calc(100svh-64px)] w-[360px] overflow-y-auto border-l p-4 shadow-lg">
      <header className="mb-4 flex items-center justify-between">
        <h3 className="font-semibold">{t("slideSense.usage.nodeDrawerTitle")}</h3>
        <button
          aria-label={t("common.close")}
          className="text-muted-foreground hover:text-foreground"
          type="button"
          onClick={onClose}
        >
          ×
        </button>
      </header>

      {isLoading && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 w-full rounded-md" />
            ))}
          </div>
          <Skeleton className="h-4 w-24" />
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        </div>
      )}

      {isError && (
        <UsageErrorState isRetrying={isFetching} variant="compact" onRetry={() => refetch()} />
      )}

      {data && !isError && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-2">
            <Stat label={t("slideSense.usage.kpi.uses")} value={data.kpi.uses} />
            <Stat label={t("slideSense.usage.kpi.exports")} value={data.kpi.exports} />
            <Stat label={t("slideSense.usage.kpi.presentations")} value={data.kpi.presentations} />
            <Stat label={t("slideSense.usage.kpi.uniqueUsers")} value={data.kpi.unique_users} />
          </div>

          <h4 className="text-muted-foreground mb-2 text-xs uppercase">
            {t("slideSense.usage.topUsers")}
          </h4>
          <ul className="text-sm">
            {data.top_users.slice(0, 5).map((u) => (
              <li key={u.user_id} className="flex items-center border-b py-1 last:border-0">
                <span className="truncate">{u.user_email ?? u.user_id}</span>
                <span className="text-muted-foreground ml-auto tabular-nums">
                  {u.exports} {t("slideSense.usage.kpi.exports").toLowerCase()}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </aside>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-muted rounded p-2">
      <div className="text-muted-foreground text-[10px] uppercase">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  )
}
